// frontend/src/pages/admin/GroupFeeConfigPage.tsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Select,
  InputNumber,
  Switch,
  Button,
  Space,
  message,
  Divider,
  Typography,
  Row,
  Col,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import * as feeConfigService from '../../services/feeConfig';
import * as groupsService from '../../services/groups';

const { Title, Text } = Typography;
const { Option } = Select;

const GroupFeeConfigPage: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [groupName, setGroupName] = useState('');

  useEffect(() => {
    if (groupId) {
      loadGroupInfo();
      loadFeeConfig();
    }
  }, [groupId]);

  const loadGroupInfo = async () => {
    try {
      const response = await groupsService.adminGetGroup(groupId!);
      if (response.success) {
        setGroupName(response.data.name);
      }
    } catch (error: any) {
      message.error('加载分组信息失败');
    }
  };

  const loadFeeConfig = async () => {
    setLoading(true);
    try {
      const response = await feeConfigService.adminGetGroupFeeConfig(groupId!);
      if (response.success) {
        form.setFieldsValue(response.data);
      }
    } catch (error: any) {
      message.error('加载费率配置失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      await feeConfigService.adminUpdateGroupFeeConfig(groupId!, values);
      message.success('费率配置保存成功');
    } catch (error: any) {
      if (error.errorFields) {
        return; // 表单验证错误
      }
      message.error(error.response?.data?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const feeTypeOptions = [
    { label: '百分比费率', value: 'percentage' },
    { label: '固定费用', value: 'fixed' },
    { label: '组合费率 (百分比+固定)', value: 'combined' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/groups')}>
              返回
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              {groupName} - 费率配置
            </Title>
          </Space>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSubmit}
            loading={saving}
          >
            保存配置
          </Button>
        </div>

        <Card title="默认费率" loading={loading}>
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="费率类型"
                  name={['default_fee', 'type']}
                  rules={[{ required: true, message: '请选择费率类型' }]}
                  initialValue="combined"
                >
                  <Select placeholder="选择费率类型">
                    {feeTypeOptions.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.default_fee?.type !== currentValues.default_fee?.type
                  }
                >
                  {({ getFieldValue }) => {
                    const feeType = getFieldValue(['default_fee', 'type']);
                    return (
                      (feeType === 'percentage' || feeType === 'combined') && (
                        <Form.Item
                          label="百分比费率 (%)"
                          name={['default_fee', 'percentage_rate']}
                          rules={[
                            { required: true, message: '请输入百分比费率' },
                            { type: 'number', min: 0, max: 100, message: '费率必须在0-100之间' },
                          ]}
                          initialValue={2.9}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            placeholder="如: 2.9"
                            step={0.1}
                            precision={2}
                          />
                        </Form.Item>
                      )
                    );
                  }}
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.default_fee?.type !== currentValues.default_fee?.type
                  }
                >
                  {({ getFieldValue }) => {
                    const feeType = getFieldValue(['default_fee', 'type']);
                    return (
                      (feeType === 'fixed' || feeType === 'combined') && (
                        <Form.Item
                          label="固定费用 (分)"
                          name={['default_fee', 'fixed_amount']}
                          rules={[{ required: true, message: '请输入固定费用' }]}
                          initialValue={30}
                          tooltip="以最小货币单位计，如30表示$0.30"
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            placeholder="如: 30 (表示$0.30)"
                            step={1}
                            precision={0}
                            min={0}
                          />
                        </Form.Item>
                      )
                    );
                  }}
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Title level={5}>费用限制 (可选)</Title>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="最低费用 (分)"
                  name={['default_fee', 'min_fee']}
                  tooltip="手续费的最低金额"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="最低费用"
                    step={1}
                    precision={0}
                    min={0}
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  label="最高费用 (分)"
                  name={['default_fee', 'max_fee']}
                  tooltip="手续费的最高金额"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="最高费用"
                    step={1}
                    precision={0}
                    min={0}
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  label="费用上限 (分)"
                  name={['default_fee', 'cap']}
                  tooltip="手续费的绝对上限"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="上限"
                    step={1}
                    precision={0}
                    min={0}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Title level={5}>其他设置</Title>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="启用自定义费率"
                  name="custom_fee_enabled"
                  valuePropName="checked"
                  initialValue={false}
                  tooltip="是否允许为不同支付方式设置不同费率"
                >
                  <Switch />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="将费用转嫁给客户"
                  name="pass_fee_to_customer"
                  valuePropName="checked"
                  initialValue={false}
                  tooltip="是否将手续费加到客户支付金额中"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        <Card>
          <Title level={5}>费率计算说明</Title>
          <Text>
            <ul>
              <li>
                <strong>百分比费率:</strong> 手续费 = 交易金额 × 百分比费率
              </li>
              <li>
                <strong>固定费用:</strong> 手续费 = 固定金额
              </li>
              <li>
                <strong>组合费率:</strong> 手续费 = (交易金额 × 百分比费率) + 固定金额
              </li>
              <li>
                <strong>费用限制:</strong> 计算后的手续费会应用最低/最高/上限的限制
              </li>
            </ul>
          </Text>
          <Text type="secondary">
            示例: 2.9% + $0.30 表示百分比费率为2.9%，固定费用为30分 (0.30美元)
          </Text>
        </Card>
      </Space>
    </div>
  );
};

export default GroupFeeConfigPage;
